import React, { useLayoutEffect, useState } from 'react';
import { useWindowDimensions, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { nanoid } from 'nanoid/non-secure';
import { BodyText } from '../Text';
import { Overlay } from '../Overlay';
import { useMeasuredViewStyle, usePressableState } from '../../hooks';
import { Trigger } from './Select.trigger';
import { styles } from './Select.styles';
import type { SelectProps } from './Select.types';
import { SelectContent } from './Select.content';
import type { PlacementOptions } from '../../hooks/useAutomaticPlacement';
import {
  useAutomaticPlacement,
  AutomaticallyPlacedView,
} from '../../hooks/useAutomaticPlacement';

export const Select = (props: SelectProps): React.ReactElement | null => {
  const {
    label,
    value,
    options,
    onSelect,
    variation = 'standard',
    testID,
    isLoading,
  } = props;
  const selectId = React.useMemo(() => `sg-select-${nanoid()}`, []);
  const [opened, setShowing] = useState(false);
  const [placement, setCurrentPlacement] = useState<PlacementOptions>('bottom');
  const selectedOption = options.find((opt) => opt.value === value)?.label;
  const triggerRef = React.useRef(null);
  const contentRef = React.useRef(null);

  const { isInteracting } = usePressableState(triggerRef);

  // --- options with same width as trigger ---
  const [width, setWidth] = useState(0);
  const { onLayout, measuredViewStyle } = useMeasuredViewStyle({
    reference: triggerRef,
    width,
    setWidth,
  });

  // --- automatically positions select where available ---
  const windowDimensions = useWindowDimensions();
  const placementX = useSharedValue(0);
  const placementY = useSharedValue(0);
  const { updatePlacement, updatePlacementDebounced } = useAutomaticPlacement({
    placementX,
    placementY,
    triggerRef,
    contentRef,
    placement,
    setCurrentPlacement,
  });

  useLayoutEffect(() => {
    if (!opened) return;
    updatePlacement();
  }, [
    opened,
    updatePlacement,
    windowDimensions.width,
    windowDimensions.height,
  ]);

  // --- Callbacks ---
  const toggleSelect = React.useCallback(() => {
    setShowing(!opened);
  }, [opened, setShowing]);
  const closeSelect = React.useCallback(() => {
    setShowing(false);
  }, [setShowing]);
  const selectOption = React.useCallback(
    (option) => {
      onSelect(option);
      closeSelect();
    },
    [onSelect, closeSelect],
  );

  return (
    <View onLayout={updatePlacementDebounced}>
      {label ? (
        <BodyText
          size={4}
          style={variation === 'box' && styles.labelWithSpacing}
        >
          {label}
        </BodyText>
      ) : null}
      <Trigger
        testID={testID}
        selectId={selectId}
        ref={triggerRef}
        label={label}
        opened={opened}
        selectedOption={selectedOption}
        isInteracting={isInteracting}
        onPress={toggleSelect}
        onLayout={() => {
          onLayout();
          updatePlacement();
        }}
        variation={variation}
        isLoading={isLoading}
      />
      <Overlay
        show={opened}
        triggerRef={triggerRef}
        contentRef={contentRef}
        dismiss={closeSelect}
      >
        <AutomaticallyPlacedView
          placementX={placementX}
          placementY={placementY}
          style={styles.select}
        >
          <SelectContent
            selectId={selectId}
            measuredViewStyle={measuredViewStyle}
            opened={opened}
            contentRef={contentRef}
            value={value}
            options={options}
            onSelect={selectOption}
            onLayout={updatePlacementDebounced}
          />
        </AutomaticallyPlacedView>
      </Overlay>
    </View>
  );
};
